import React, { useEffect, useState } from "react";
import axios from "axios";
import { useLanguage } from "@/lib/LanguageContext";
import { HOSPITAL, whatsappLink } from "@/lib/i18n";
import { MapPin, Phone, Mail, Facebook, Instagram, Youtube, Sparkles } from "lucide-react";
import ChangelogModal from "@/admin/ChangelogModal";

const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");

const Footer = () => {
  const { t, lang } = useLanguage();

  const [version, setVersion] = useState("");
  const [changelog, setChangelog] = useState([]);
  const [changelogOpen, setChangelogOpen] = useState(false);

  useEffect(() => {
    let active = true;
    axios
      .get(`${BACKEND_URL}/api/changelog`)
      .then((r) => {
        if (!active) return;
        setVersion(r.data?.current || "");
        setChangelog(r.data?.items || []);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  const links = [
    { id: "home", label: t.nav.home, href: "#home" },
    { id: "about", label: t.nav.about, href: "#about" },
    { id: "doctors", label: t.nav.doctors, href: "#doctors" },
    { id: "news", label: t.nav.news, href: "#news" },
    { id: "gallery", label: t.nav.gallery, href: "#gallery" },
    { id: "contact", label: t.nav.contact, href: "#contact" },
  ];

  const services = [
    lang === "id" ? "IGD 24 Jam" : "24 Hour ER",
    lang === "id" ? "Poliklinik Spesialis" : "Specialist Clinic",
    lang === "id" ? "Rawat Inap" : "Inpatient Care",
    lang === "id" ? "Laboratorium" : "Laboratory",
    lang === "id" ? "Radiologi" : "Radiology",
    lang === "id" ? "Medical Check-Up" : "Medical Check-Up",
  ];

  return (
    <footer
      data-testid="site-footer"
      className="bg-slate-900 text-slate-300 pt-16 pb-8 relative overflow-hidden"
    >
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-600 to-transparent" />

      <div className="container-x">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-10 mb-12">
          {/* Brand */}
          <div className="col-span-2">
            <div className="flex items-center gap-3 mb-5">
              <img
                src={HOSPITAL.logo}
                alt="Logo"
                className="h-12 w-12 rounded-full object-cover ring-2 ring-slate-800"
              />
              <div>
                <div className="font-heading font-bold text-white text-lg">
                  {HOSPITAL.name}
                </div>
                <div className="text-xs text-slate-400">
                  {HOSPITAL.affiliation}
                </div>
              </div>
            </div>
            <p className="text-sm leading-relaxed text-slate-400 max-w-md">
              {t.footer.about}
            </p>

            <div className="mt-6">
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">
                {t.footer.follow_us}
              </div>
              <div className="flex items-center gap-2 mb-4">
                <a
                  href={HOSPITAL.facebook_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Facebook RS Bhayangkara Tarakan"
                  title="Facebook RS Bhayangkara Tarakan"
                  data-testid="footer-facebook"
                  className="h-9 w-9 rounded-full bg-slate-800 hover:bg-blue-800 flex items-center justify-center transition"
                >
                  <Facebook className="h-4 w-4" />
                </a>
                <a
                  href={HOSPITAL.instagram_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Instagram @${HOSPITAL.instagram_handle}`}
                  title={`@${HOSPITAL.instagram_handle}`}
                  data-testid="footer-instagram"
                  className="h-9 w-9 rounded-full bg-slate-800 hover:bg-pink-600 flex items-center justify-center transition"
                >
                  <Instagram className="h-4 w-4" />
                </a>
                <a
                  href="#"
                  aria-label="Youtube"
                  data-testid="footer-youtube"
                  className="h-9 w-9 rounded-full bg-slate-800 hover:bg-blue-800 flex items-center justify-center transition"
                >
                  <Youtube className="h-4 w-4" />
                </a>
              </div>

              <a
                href={HOSPITAL.instagram_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-instagram-follow"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-pink-500 via-fuchsia-500 to-orange-400 text-white text-sm font-medium shadow-lg shadow-fuchsia-900/30 hover:from-pink-600 hover:via-fuchsia-600 hover:to-orange-500 transition"
              >
                <Instagram className="h-4 w-4" />
                <span>
                  {t.footer.follow_ig}{" "}
                  <span className="font-semibold">
                    @{HOSPITAL.instagram_handle}
                  </span>
                </span>
              </a>
            </div>
          </div>

          {/* Links */}
          <div>
            <h4 className="font-heading font-semibold text-white mb-4">
              {t.footer.quick_links}
            </h4>
            <ul className="space-y-2.5 text-sm">
              {links.map((l) => (
                <li key={l.id}>
                  <a
                    href={l.href}
                    data-testid={`footer-link-${l.id}`}
                    className="text-slate-400 hover:text-white transition"
                  >
                    {l.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-heading font-semibold text-white mb-4">
              {t.footer.services_links}
            </h4>
            <ul className="space-y-2.5 text-sm">
              {services.map((s) => (
                <li key={s} className="text-slate-400">
                  {s}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Contact strip */}
        <div className="grid md:grid-cols-3 gap-6 pt-8 border-t border-slate-800">
          <div className="flex items-start gap-3 text-sm">
            <MapPin className="h-4 w-4 text-blue-400 shrink-0 mt-0.5" />
            <span className="text-slate-400">
              {lang === "id" ? HOSPITAL.address_id : HOSPITAL.address_en}
            </span>
          </div>
          <a
            href={whatsappLink()}
            target="_blank"
            rel="noreferrer"
            className="flex items-start gap-3 text-sm hover:text-white transition"
          >
            <Phone className="h-4 w-4 text-blue-400 shrink-0 mt-0.5" />
            <span className="text-slate-400">{HOSPITAL.phone_display}</span>
          </a>
          <a
            href={`mailto:${HOSPITAL.email}`}
            className="flex items-start gap-3 text-sm hover:text-white transition"
          >
            <Mail className="h-4 w-4 text-blue-400 shrink-0 mt-0.5" />
            <span className="text-slate-400">{HOSPITAL.email}</span>
          </a>
        </div>

        <div className="mt-8 pt-6 border-t border-slate-800 text-center text-xs text-slate-500">
          <div>
            © {new Date().getFullYear()} {HOSPITAL.name}. {t.footer.rights}
          </div>
          {version && (
            <div className="mt-2 flex items-center justify-center gap-2 flex-wrap">
              <span className="text-slate-600">v{version}</span>
              <span className="text-slate-700">&middot;</span>
              <button
                onClick={() => setChangelogOpen(true)}
                data-testid="footer-changelog-btn"
                className="inline-flex items-center gap-1 text-slate-400 hover:text-white transition"
              >
                <Sparkles className="h-3 w-3" />
                {lang === "id" ? "Pembaruan terkini" : "Latest updates"}
              </button>
            </div>
          )}
        </div>
      </div>

      <ChangelogModal
        open={changelogOpen}
        onClose={() => setChangelogOpen(false)}
        current={version}
        items={changelog}
      />
    </footer>
  );
};

export default Footer;
